import { Property } from '@propertypal/shared/src/types/property';
import { getFormattedPrice } from '@propertypal/shared/src/utils/property';
import React, { CSSProperties, FunctionComponent } from 'react';
import { useTheme } from 'styled-components';
import { Bold } from '../typography';
import { Container, AltText, StatusBtn, Prefix, InBetween } from './PropertyPrice.style';

interface Props {
  as?: 'span' | 'p';
  containerStyle?: CSSProperties;
  price?: Property['price'];
  alternativeCurrencyPrice?: Property['alternativeCurrencyPrice'];
  fontSize: number;
  altFontSize?: number;
  primaryColor?: string;
  secondaryColor?: string;
  status?: Property['status'];
  comingSoonText?: string;
  statusButton?: boolean;
  prefixBreak?: boolean;
  prefixColor?: string;
  suffixColor?: string;
}

const SOLD_STATUS = ['saleAgreed', 'sold', 'let', 'letAgreed'];

const PropertyPrice: FunctionComponent<Props> = (props) => {
  const { price, alternativeCurrencyPrice, fontSize } = props;
  const theme = useTheme();
  const isSold = !!(props.status && SOLD_STATUS.includes(props.status.key));

  if (isSold) {
    return (
      <Container
        as={props.as}
        color={props.primaryColor || theme.textDark}
        style={{ ...props.containerStyle, fontSize }}
      >
        {props.statusButton && (
          <StatusBtn className="pp-btn" data-testid="status-btn">
            {props.status?.text}
          </StatusBtn>
        )}

        {!props.statusButton && <Bold>{props.status?.text}</Bold>}
      </Container>
    );
  }

  if (price && (price.priceHidden || price.priceOnApplication)) {
    return (
      <Container
        as={props.as}
        color={props.primaryColor || theme.textDark}
        style={{ ...props.containerStyle, fontSize }}
        data-testid="poaPrice"
      >
        {price.hiddenPriceText || 'POA'}
      </Container>
    );
  }

  if (price && price.minPrice && price.minPrice !== price.maxPrice) {
    const minPrice = getFormattedPrice(price.minPrice, price.currencySymbol);
    const maxPrice = getFormattedPrice(price.maxPrice, price.currencySymbol);

    const altMinPrice = alternativeCurrencyPrice
      ? getFormattedPrice(alternativeCurrencyPrice.minPrice, alternativeCurrencyPrice.currencySymbol)
      : '';
    const altMaxPrice = alternativeCurrencyPrice
      ? getFormattedPrice(alternativeCurrencyPrice.maxPrice, alternativeCurrencyPrice.currencySymbol)
      : '';

    return (
      <Container
        as={props.as}
        color={props.secondaryColor || theme.textLight}
        style={{ ...props.containerStyle, fontSize }}
      >
        <span>
          <Prefix
            $prefixBreak={props.prefixBreak}
            $fontSize={props.altFontSize}
            $color={props.prefixColor || props.primaryColor || undefined}
          >{`From `}</Prefix>
          <Bold color={props.primaryColor || theme.textDark} data-testid="minPrice">
            {minPrice}
          </Bold>
          <InBetween $prefixBreak={props.prefixBreak} $fontSize={props.altFontSize}>{` to `}</InBetween>
          <Bold color={props.primaryColor || theme.textDark} data-testid="maxPrice">
            {maxPrice}
          </Bold>
        </span>

        {!!altMinPrice && !!altMaxPrice && (
          <AltText data-testid="altCurrencyText" $fontSize={props.altFontSize}>
            {`(Est.From `}
            <Bold color={theme.textDark} data-testid="altMinPrice">
              {altMinPrice}
            </Bold>
            <InBetween $prefixBreak={props.prefixBreak} $fontSize={props.altFontSize}>{` to `}</InBetween>
            <Bold color={theme.textDark} data-testid="altMaxPrice">
              {altMaxPrice}
            </Bold>
            )
          </AltText>
        )}
      </Container>
    );
  }

  if (price && price.price) {
    const formattedPrice = getFormattedPrice(price.price, price.currencySymbol);
    const altPrice = alternativeCurrencyPrice
      ? getFormattedPrice(alternativeCurrencyPrice.price, alternativeCurrencyPrice.currencySymbol)
      : '';

    return (
      <Container
        as={props.as}
        color={props.primaryColor || theme.textLight}
        style={{ ...props.containerStyle, fontSize }}
      >
        <span>
          {price.pricePrefix && (
            <Prefix
              data-testid="price-prefix"
              $prefixBreak={props.prefixBreak}
              $fontSize={props.altFontSize}
              $color={props.prefixColor || props.primaryColor || undefined}
            >{`${price.pricePrefix} `}</Prefix>
          )}
          <Bold color={props.primaryColor || theme.textDark}>{formattedPrice}</Bold>
          {price.priceSuffix && (
            <Prefix
              data-testid="price-suffix"
              $prefixBreak={props.prefixBreak}
              $fontSize={props.altFontSize}
              $color={props.suffixColor || props.primaryColor || undefined}
            >{` ${price.priceSuffix}`}</Prefix>
          )}
        </span>

        {!!alternativeCurrencyPrice && !!altPrice && (
          <AltText data-testid="altCurrencyText" $prefixBreak={props.prefixBreak} $fontSize={props.altFontSize}>
            (Est.{alternativeCurrencyPrice.pricePrefix && ` ${alternativeCurrencyPrice.pricePrefix} `}
            <Bold color={props.primaryColor || theme.textDark}>{altPrice}</Bold>
            {alternativeCurrencyPrice.priceSuffix && ` ${alternativeCurrencyPrice.priceSuffix}`})
          </AltText>
        )}
      </Container>
    );
  }

  if (props.comingSoonText) {
    return (
      <Container
        as={props.as}
        color={props.primaryColor || theme.textDark}
        style={{ ...props.containerStyle, fontSize }}
      >
        <Bold>{props.comingSoonText}</Bold>
      </Container>
    );
  }

  return null;
};

export default PropertyPrice;
